import {
	Loadable,
	RecoilState,
	Resetter,
	SetterOrUpdater,
	useRecoilState,
	useRecoilStateLoadable,
	useRecoilValue,
	useRecoilValueLoadable,
	useResetRecoilState,
	useSetRecoilState
} from "recoil";
import {connectedStore} from "./ConnectedStore";
import Atom from "shlibs/dist/Atom";
import {useEffect, useState} from "react";
import StackAtom from "shlibs/dist/StackAtom";


//***************************************************************/
//* Hooks for using App State */
//* AppState is directly mapped to Recoil State, without modification
//* Hooks are provided entirely for purposes of better legibility of code.
//* See: https://skillshot-fdi-mb.atlassian.net/wiki/spaces/MONSTERBET/pages/70189059/State+Management+and+Services
//***************************************************************/

/**
 * Mapped to useRecoilState
 * @see useRecoilState
 */
export function useConnectedState<T>(recoilState: RecoilState<T>): [T, SetterOrUpdater<T>]{
	const [val,setter] = useRecoilState(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return [val,setter];
}
/**
 * Mapped to useRecoilValueLoadable
 * @see useRecoilValueLoadable
 */
export function useConnectedStateLoadable<T>(recoilState: RecoilState<T>): [Loadable<T>, SetterOrUpdater<T>]{
	const [val,setter] = useRecoilStateLoadable(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return [val,setter];
}
/**
 * Mapped to useRecoilValue
 * @see useRecoilValue
 */
export function useConnectedValue<T>(recoilState: RecoilState<T>): T{
	const [val,setter] = useRecoilState(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return val;
}
/**
 * Mapped to useRecoilValueLoadable
 * @see useRecoilValueLoadable
 */
export function useConnectedValueLoadable<T>(recoilState: RecoilState<T>): Loadable<T>{
	const [val,setter] = useRecoilStateLoadable(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return val;
}
/**
 * Mapped to useSetRecoilState
 * @see useSetRecoilState
 */
export function useSetConnectedState<T>(recoilState: RecoilState<T>): SetterOrUpdater<T>{
	const setter = useSetRecoilState(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return setter;
}
/**
 * Mapped to useResetRecoilState
 * @see useResetRecoilState
 */
export function useResetConnectedState(recoilState: RecoilState<any>): Resetter{
	const setter = useSetRecoilState(recoilState);
	connectedStore.addRecoilState(recoilState,setter);
	return useResetRecoilState(recoilState);
}


/*Links shlibs Atoms to State*/
export function useExternalAtom<T>(externalAtom:Atom<T>):[T,(v:T)=>void]{
	const [atomVal,setAtom] = useState(externalAtom.get());
	useEffect(()=>{
		externalAtom.subscribe((v)=> {
			setAtom(v);
		});
	},[])
	const setter = (newVal:T)=>{
		externalAtom.set(newVal);
		//state will be updated through externalAtom Listener.
	}
	return [atomVal,setter];
}
